import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Card,
    CrossIcon,
    IconButton,
    Menu,
    Pane,
    SearchInput,
    Spinner,
    Text,
    WarningSignIcon,
} from "evergreen-ui";
import { ColorPalette } from "../context/Theme";

/** Lifecycle of the typeahead dropdown for the current query. */
type SearchState = "idle" | "searching" | "results" | "empty" | "error";

const DEFAULT_DEBOUNCE_MS = 250;

/** Extra context passed to the render callbacks. */
export interface RenderContext {
    /** True when a `scopeId` is set (results are narrowed to one scope). */
    scoped: boolean;
}

export interface AutocompleteMultiSelectorProps<T> {
    /**
     * Runs the typeahead query. Caller supplies the data layer (already trims).
     * `scopeId` is forwarded from the prop below so the data layer can narrow the
     * query to one scope; it is undefined for the unscoped case.
     */
    search: (query: string, scopeId?: string) => Promise<T[]>;
    /** Stable id for an item — used as the React key, for dedup, and by onRemove. */
    getId: (item: T) => string;
    /** Renders an item in the dropdown. */
    renderOption: (item: T, ctx: RenderContext) => React.ReactNode;
    /** Renders an item's chip label. Defaults to `renderOption`. */
    renderChip?: (item: T, ctx: RenderContext) => React.ReactNode;
    /** aria-label for a chip's remove button. Defaults to "Remove". */
    getRemoveLabel?: (item: T) => string;

    /** Currently selected items, rendered as removable chips. */
    selected: T[];
    /**
     * Called when the user picks a not-yet-selected result. The component
     * guarantees uniqueness — already-selected items are filtered out of the
     * dropdown and re-selecting one is a no-op — so callers can append blindly.
     */
    onAdd: (item: T) => void;
    /** Called with the item id when the user removes a chip. */
    onRemove: (id: string) => void;

    /**
     * When set, results are scoped to this value (forwarded to `search`) and
     * `ctx.scoped` is true. Changing it clears the search state and fires
     * `onScopeChange` so the owner can drop selections made under the old scope.
     */
    scopeId?: string;
    /**
     * Called after the component clears its search state on a `scopeId` change.
     * Should be stable (e.g. wrapped in `useCallback`) — it's in the reset
     * effect's dependency array, so an unstable reference re-runs that effect
     * every render. The reset itself is still correct either way (a ref guard
     * ensures it only fires on an actual `scopeId` change), so this is a
     * performance nicety, not a correctness requirement.
     */
    onScopeChange?: () => void;

    placeholder?: string;
    /** Dropdown text when the query returns no matches. */
    emptyText?: (query: string) => React.ReactNode;
    /** Dropdown text when every match is already selected. */
    allSelectedText?: React.ReactNode;
    /** Dropdown text when the search request fails. */
    errorText?: React.ReactNode;
    /** Dropdown text while a search is in flight. */
    searchingText?: React.ReactNode;
    maxWidth?: number;
    debounceMs?: number;
    searchInputTestId?: string;
    selectedItemsTestId?: string;
}

/**
 * Reusable typeahead multi-selector with removable chips, generic over the item
 * type. Self-contained: it owns the search/dropdown interaction (debounce,
 * stale-response guard, click-outside dismissal) internally, de-duplicates
 * selections (an item can only be picked once), and resets on a `scopeId`
 * change. Callers supply a `search` function, item rendering via render props,
 * and the selection state + add/remove callbacks. The owning feature keeps
 * `selected` so it can drive the rest of its UI.
 */
export function AutocompleteMultiSelector<T>({
    search,
    getId,
    renderOption,
    renderChip,
    getRemoveLabel = () => "Remove",
    selected,
    onAdd,
    onRemove,
    scopeId,
    onScopeChange,
    placeholder = "Type to search...",
    emptyText = (query) => `No matches for “${query}”.`,
    allSelectedText = "All matches are already selected.",
    errorText = "Couldn't complete the search. Please try again.",
    searchingText = "Searching...",
    maxWidth = 620,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    searchInputTestId,
    selectedItemsTestId,
}: AutocompleteMultiSelectorProps<T>): JSX.Element {
    const ctx: RenderContext = { scoped: scopeId !== undefined };
    const chipRenderer = renderChip ?? renderOption;

    const [searchText, setSearchText] = useState<string>("");
    const [searchResults, setSearchResults] = useState<T[]>([]);
    const [searchState, setSearchState] = useState<SearchState>("idle");
    // Lets a click outside the search area hide the dropdown without clearing
    // the query; reset whenever the user types again.
    const [dropdownDismissed, setDropdownDismissed] = useState<boolean>(false);

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Guards against a stale (slower) search response overwriting a newer one.
    const searchSeqRef = useRef<number>(0);
    const containerRef = useRef<HTMLDivElement | null>(null);
    // Tracks the scope so a change can reset state (starts equal to the initial
    // scopeId so mount is not treated as a change).
    const scopeIdRef = useRef<string | undefined>(scopeId);

    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    // Evergreen's SearchInput + Card has no built-in click-outside handling, so
    // dismiss the dropdown when the user clicks anywhere outside the search area.
    useEffect(() => {
        const handlePointerDown = (event: MouseEvent) => {
            const container = containerRef.current;
            if (container && !container.contains(event.target as Node)) {
                setDropdownDismissed(true);
            }
        };
        document.addEventListener("mousedown", handlePointerDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
        };
    }, []);

    // A scope change invalidates the current search and any selection made under
    // the previous scope: clear the search state here and let the owner clear its
    // selection via onScopeChange. Guarded so it never fires on the initial mount.
    useEffect(() => {
        if (scopeIdRef.current === scopeId) {
            return;
        }
        scopeIdRef.current = scopeId;
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        setSearchText("");
        setSearchResults([]);
        setSearchState("idle");
        setDropdownDismissed(false);
        onScopeChange?.();
    }, [scopeId, onScopeChange]);

    const runSearch = useCallback(async (text: string) => {
        const seq = ++searchSeqRef.current;
        const trimmed = text.trim();
        if (trimmed.length === 0) {
            setSearchResults([]);
            setSearchState("idle");
            return;
        }
        setSearchState("searching");
        try {
            const results = await search(trimmed, scopeId);
            if (seq === searchSeqRef.current) {
                setSearchResults(results);
                setSearchState(results.length > 0 ? "results" : "empty");
            }
        } catch {
            if (seq === searchSeqRef.current) {
                setSearchResults([]);
                setSearchState("error");
            }
        }
    }, [search, scopeId]);

    const onSearchTextChange = useCallback((text: string) => {
        setSearchText(text);
        // Typing re-opens a dropdown the user previously clicked away from.
        setDropdownDismissed(false);
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        // Blank input closes the dropdown immediately; otherwise show the spinner
        // right away so there's feedback during the debounce window.
        if (text.trim().length === 0) {
            setSearchResults([]);
            setSearchState("idle");
            return;
        }
        setSearchState("searching");
        debounceRef.current = setTimeout(() => {
            void runSearch(text);
        }, debounceMs);
    }, [runSearch, debounceMs]);

    const handleSelect = useCallback((item: T) => {
        // Built-in de-duplication: an item can only be selected once, so
        // re-selecting one that's already chosen is a no-op.
        if (selected.some((s) => getId(s) === getId(item))) {
            return;
        }
        onAdd(item);
        // Reset the search field once an item is chosen.
        setSearchText("");
        setSearchResults([]);
        setSearchState("idle");
    }, [onAdd, selected, getId]);

    const dropdownVisible = searchText.trim().length > 0 && searchState !== "idle" && !dropdownDismissed;
    // Already-selected items are hidden from the results so they can't be picked twice.
    const selectedIds = new Set(selected.map(getId));
    const visibleResults = searchResults.filter((item) => !selectedIds.has(getId(item)));

    return (
        <>
            <Pane ref={containerRef} position={"relative"} maxWidth={maxWidth}>
                <SearchInput
                    width={"100%"}
                    placeholder={placeholder}
                    value={searchText}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchTextChange(e.target.value)}
                    data-testid={searchInputTestId}
                />
                {dropdownVisible && (
                    <Card
                        position={"absolute"}
                        top={"100%"}
                        left={0}
                        right={0}
                        zIndex={20}
                        marginTop={4}
                        background={"white"}
                        elevation={2}
                        borderRadius={6}
                        overflow={"hidden"}
                    >
                        {searchState === "searching" && (
                            <Pane display={"flex"} alignItems={"center"} padding={12}>
                                <Spinner size={16} marginRight={8} />
                                <Text fontSize={12} color={"muted"}>{searchingText}</Text>
                            </Pane>
                        )}
                        {searchState === "results" && visibleResults.length > 0 && (
                            <Menu>
                                <Menu.Group>
                                    {visibleResults.map((item) => (
                                        <Menu.Item
                                            key={getId(item)}
                                            height={32}
                                            onSelect={() => handleSelect(item)}
                                        >
                                            <Text fontSize={12}>{renderOption(item, ctx)}</Text>
                                        </Menu.Item>
                                    ))}
                                </Menu.Group>
                            </Menu>
                        )}
                        {searchState === "results" && visibleResults.length === 0 && (
                            <Pane padding={12}>
                                <Text fontSize={12} color={"muted"}>{allSelectedText}</Text>
                            </Pane>
                        )}
                        {searchState === "empty" && (
                            <Pane padding={12}>
                                <Text fontSize={12} color={"muted"}>{emptyText(searchText.trim())}</Text>
                            </Pane>
                        )}
                        {searchState === "error" && (
                            <Pane display={"flex"} alignItems={"center"} padding={12}>
                                <WarningSignIcon color={ColorPalette.statusBad} marginRight={8} flexShrink={0} />
                                <Text fontSize={12} color={ColorPalette.statusBad}>
                                    {errorText}
                                </Text>
                            </Pane>
                        )}
                    </Card>
                )}
            </Pane>

            {selected.length > 0 && (
                <Pane
                    display={"flex"}
                    flexWrap={"wrap"}
                    gap={8}
                    marginTop={16}
                    maxWidth={maxWidth}
                    data-testid={selectedItemsTestId}
                >
                    {selected.map((item) => (
                        <Pane
                            key={getId(item)}
                            display={"flex"}
                            alignItems={"center"}
                            background={ColorPalette.primaryBackgroundLight}
                            borderRadius={12}
                            paddingLeft={12}
                            paddingRight={4}
                            paddingY={2}
                        >
                            {chipRenderer(item, ctx)}
                            <IconButton
                                icon={CrossIcon}
                                appearance={"minimal"}
                                size={"small"}
                                aria-label={getRemoveLabel(item)}
                                onClick={() => onRemove(getId(item))}
                            />
                        </Pane>
                    ))}
                </Pane>
            )}
        </>
    );
}
